import axios from 'axios';

/*
 * Google Search Console endpoints (backend/search_console*.py, prefix
 * /search-console).  Same base URL and bearer-token pattern as apiAnalytics.js.
 *
 * Every getter resolves to the response body or { error: true } so a view can
 * render its other sections when one call fails.  Topics can take a minute the
 * first time for a site (it fetches and embeds the live pages); later calls are
 * cached for 15 minutes on the server.
 */
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const api = axios.create({ baseURL: API_BASE_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const get = async (path, params) => {
  try {
    const response = await api.get(`/search-console${path}`, { params });
    return response.data || {};
  } catch (error) {
    console.error(`Search Console ${path} error:`, error.response?.data || error.message);
    return { error: true, status: error.response?.status };
  }
};

/* -> { days, sites: [{ domain_id, site, clicks, impressions, position, prev_*, tracked_queries, close_queries }] } */
export const getRankingsOverview = (days = 28) => get('/overview', { days });

/* -> { opportunities: [{ domain_id, query, position, impressions, est_monthly_searches, gain_top3_clicks, gain_top3_usd, top_page, ... }] } */
export const getFleetOpportunities = (limit = 50) => get('/opportunities', { limit });

/* The same list from a stored snapshot (fast), rebuilt in the background when over 6 hours old.
   -> { opportunities, updated_at, age_hours, stale, building }; building with an empty list = first build running. */
export const getOpportunitySnapshot = (limit = 50) => get('/opportunities/snapshot', { limit });

/* -> { window, totals, model, untapped, queries, pages, tracked (with weekly_position), opportunities, low_ctr } */
export const getSiteRankings = (domainId, days = 28, n = 100) => get(`/site/${encodeURIComponent(domainId)}`, { days, n });

/* -> { queries: [{ query, impressions, clicks, pages: [{ page, share, position }], top_page_switches, suggestion }] } */
export const getCannibalization = (domainId) => get(`/site/${encodeURIComponent(domainId)}/cannibalization`);

/* -> { pages: [{ page, impressions, clicks, focus, flag, topics, main_query, title_missing, missing_words }] } */
export const getPageDiagnosis = (domainId) => get(`/site/${encodeURIComponent(domainId)}/pages`);

/* -> { on_topic, content_vs_demand_gap, early_data, clusters, pages, map: { pages, queries } } */
export const getTopics = (domainId) => get(`/site/${encodeURIComponent(domainId)}/topics`);
